import { z } from 'zod'
import { Id, UpdateDate } from './common-schemas'
import { GarItem } from './gar-item'

export namespace NormDoc {
  export interface Data {
    /** @description Identifier of the normative document */
    readonly id: number

    /** @description Document name */
    readonly name?: string

    /** @description Document date */
    readonly date: Date

    /** @description Document number */
    readonly number: string

    /** @description Document type */
    readonly type: number

    /** @description Document kind */
    readonly kind: number

    /** @description Update date */
    readonly updateDate: Date

    /** @description Name of the authority that created the normative document */
    readonly orgName?: string

    /** @description State registration number */
    readonly regNum?: string

    /** @description State registration date */
    readonly regDate?: Date

    /** @description Date of entry into force of the normative document */
    readonly accDate?: Date

    /** @description Comment */
    readonly comment?: string
  }
}

/** @description Information on the normative document that serves as the basis for assigning a name to an address element */
export class NormDoc extends GarItem<'NormDoc', NormDoc.Data> {
  static readonly SCHEMA: GarItem.Schema<NormDoc.Data> = z
    .strictObject({
      ID: Id,

      NAME: z.string().describe('Document name').optional(),

      DATE: z.coerce.date<string>().describe('Document date'),

      NUMBER: z.string().describe('Document number'),

      TYPE: z.coerce.number<string>().int().describe('Document type'),

      KIND: z.coerce.number<string>().int().describe('Document kind'),

      UPDATEDATE: UpdateDate,

      ORGNAME: z
        .string()
        .optional()
        .describe('Name of the authority that created the normative document'),

      REGNUM: z.string().optional().describe('State registration number'),

      REGDATE: z.coerce
        .date<string>()
        .optional()
        .describe('State registration date'),

      ACCDATE: z.coerce
        .date<string>()
        .optional()
        .describe('Date of entry into force of the normative document'),

      COMMENT: z.string().optional().describe('Comment'),
    })
    .transform<NormDoc.Data>(attributes => ({
      id: attributes.ID,
      name: attributes.NAME,
      date: attributes.DATE,
      number: attributes.NUMBER,
      type: attributes.TYPE,
      kind: attributes.KIND,
      updateDate: attributes.UPDATEDATE,
      orgName: attributes.ORGNAME,
      regNum: attributes.REGNUM,
      regDate: attributes.REGDATE,
      accDate: attributes.ACCDATE,
      comment: attributes.COMMENT,
    }))

  readonly type = 'NormDoc'

  protected get schema() {
    return NormDoc.SCHEMA
  }
}
